import React, { createContext, useContext, useState, ReactNode } from "react";
import "./Modal.scss";

type CustomModalContextType = {
  showCustomModal: <T>(
    title: string,
    children: ReactNode,
    submitText?: string,
    showActionButtons?: boolean,
    subtitle?: string,
    onSubmit?: () => Promise<void>
  ) => Promise<CustomModalResult<T>>;
};

const CustomModalContext = createContext<CustomModalContextType | undefined>(
  undefined
);

export interface CustomModalResult<T> {
  state: CustomModalState;
  data?: T;
}

export enum CustomModalState {
  CANCELED,
  CONFIRMED,
  CLOSED,
}

interface CustomModalData<T> {
  id: string;
  title: string;
  submitText: string;
  showActionButtons: boolean;
  subtitle?: string;
  children: ReactNode;
  resolve: (result: CustomModalResult<T>) => void;
}

export const CustomModalProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const [modalState, setModalState] = useState<CustomModalData<any>[]>([]);

  const showCustomModal = <T,>(
    title: string,
    children: ReactNode,
    submitButtonText?: string,
    showActionButtons?: boolean,
    subtitle?: string,
  ) => {
    return new Promise<CustomModalResult<T>>((resolve) => {
      const id = Math.random().toString(36).substring(2, 9);

      const submitText = submitButtonText ? submitButtonText : 'Bestätigen';
      const showActionButtonsParam =
        showActionButtons != undefined ? showActionButtons : true;

      setModalState((prev) => [
        ...prev,
        {
          id,
          title,
          submitText,
          showActionButtons: showActionButtonsParam,
          subtitle,
          children,
          resolve,
        },
      ]);
    });
  };

  const closeCustomModal = <T,>(id: string, state: CustomModalState, data?: T) => {
    setModalState((prev) => {
      const modal = prev.find((m) => m.id === id);
      if (modal) {
        modal.resolve({ state, data });
      }
      return prev.filter((m) => m.id !== id);
    });
  };

  return (
    <CustomModalContext.Provider value={{ showCustomModal }}>
      {children}
      {modalState.map((modal) => (
        <div
          key={modal.id}
          className="modal-wrapper"
          onClick={() => closeCustomModal(modal.id, CustomModalState.CANCELED)}
        >
          <div
            className="modal feedboost-modal-bounce-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-title">{modal.title}</div>
            <p className="modal-subtitle">{modal.subtitle}</p>
            <div className="modal-content">
              {React.cloneElement(modal.children as React.ReactElement)}
            </div>
            {modal.showActionButtons && (
              <div className="modal-actions">
                <button
                  className="modal-button modal-button-cancel"
                  onClick={() => closeCustomModal(modal.id, CustomModalState.CANCELED)}
                >
                  Abbrechen
                </button>
                <button
                  className="modal-button"
                  onClick={() => closeCustomModal(modal.id, CustomModalState.CONFIRMED)}
                >
                  {modal.submitText}
                </button>
              </div>
            )}
          </div>
        </div>
      ))}
    </CustomModalContext.Provider>
  );
};

export const useCustomModal = (): CustomModalContextType => {
  const context = useContext(CustomModalContext);
  if (!context) {
    throw new Error('useCustomModal must be used within a CustomModalProvider');
  }
  return context;
};